import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'ncoded-component-library';
import FloatingActions from 'components/FloatingActions';
import { LCW_PREVIEW_MODE_ROUTE } from 'router/subrouters/ListingCreationWizard/utils';
import PreviewMissingFieldsContext from '../../providers/PreviewMissingFields/PreviewMissingFields.context';

import './PreviewMissingFieldsBar.styles.scss';

/** The form view sits alongside preview mode under the same listing route. */
const FORM_VIEW_ROUTE = 'default';

type PreviewMissingFieldsBarProps = {
  className?: string;
};

/**
 * Always present at the foot of preview mode, in one of three states:
 *
 * - nothing entered yet: says what the page is and asks for nothing. An empty
 *   draft is a starting point, not a shortfall.
 * - part way: counts what the public listing still needs and offers to point at
 *   it. Optional fields never reach the count, and the three list sections
 *   count as the single outstanding item they are.
 * - everything required entered: confirms leads see the whole page, and sends
 *   the broker on to the rest of the listing.
 */
const PreviewMissingFieldsBar: React.FC<PreviewMissingFieldsBarProps> = (
  props,
) => {
  const { className } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const {
    requirements,
    missingKeys,
    missingCount,
    enteredCount,
    markAllMissing,
    requestFocus,
  } = useContext(PreviewMissingFieldsContext);

  const showMissingFields = useCallback(() => {
    markAllMissing?.();

    // Authored in the order the fields appear, so the first entry is the first
    // empty required field on the page.
    const [firstMissingKey] = missingKeys || [];

    const { fieldName, focusFieldName } =
      requirements?.find(({ key }) => key === firstMissingKey) || {};

    if (fieldName) requestFocus?.(fieldName, focusFieldName);
  }, [markAllMissing, missingKeys, requestFocus, requirements]);

  const isComplete = !missingCount;
  const isUntouched = !isComplete && !enteredCount;

  const state = isComplete ? 'complete' : isUntouched ? 'untouched' : 'partial';

  const message = isComplete
    ? t('leadsSeeEverythingOnThisPage')
    : isUntouched
    ? t('thisIsThePublicPartOfTheListing')
    : t('fieldsStillNeededOnThePublicListing', { count: missingCount });

  return (
    <FloatingActions
      className={classNames(
        'preview-missing-fields-bar',
        `preview-missing-fields-bar--${state}`,
        className,
      )}
      hideSubmit
      message={message}
    >
      {state === 'partial' && (
        <Button
          // The bar sits inside the preview form, where a bare button submits.
          type="button"
          variant="link"
          /**
           * Taking focus would close whichever field is open before the click
           * is even handled, so the field the request then lands on has to
           * reopen - flashing the card at someone already sitting in it.
           */
          onMouseDown={(e) => e.preventDefault()}
          onClick={showMissingFields}
        >
          {t('showMissingFields')}
        </Button>
      )}
      {isComplete && (
        <Button
          type="button"
          onClick={() =>
            navigate(
              pathname.replace(LCW_PREVIEW_MODE_ROUTE, FORM_VIEW_ROUTE),
              { replace: true },
            )
          }
        >
          {t('continueInFormView')}
        </Button>
      )}
    </FloatingActions>
  );
};

export default PreviewMissingFieldsBar;
